import { useNavigate } from "react-router-dom";
import { ImageOff } from "lucide-react";
import { imageUrl, formatINR } from "@/lib/lamka";
import { RailHook } from "./RailHook";
import { HangingGarment } from "./HangingGarment";
import { ThriftInfo } from "./ThriftInfo";

const CARD = "w-[170px] h-[310px] sm:h-[360px] rounded-2xl";

const Ghosts = ({ img, vel, cut }) => {
  const speed = Math.abs(vel);
  if (speed < 0.035 || !img) return null;
  const n = speed > 0.12 ? 3 : 2;
  const step = Math.min(26, 10 + speed * 90) * Math.sign(vel);
  return Array.from({ length: n }, (_, k) => (
    <img key={k} src={img} alt="" aria-hidden="true" data-testid="thrift-ghost" className={`absolute top-0 left-0 ${CARD} ${cut ? "object-contain object-top" : "object-cover"} pointer-events-none`} style={{ transform: `translateX(${step * (k + 1)}px)`, opacity: 0.26 / (k + 1), filter: "blur(1px)" }} />
  ));
};

export const ThriftPiece = ({ item, cutout, index, isNew, badge = "New in your size", offset, vel, spacing, onSelect, onToggleLike }) => {
  const navigate = useNavigate();
  const img = cutout || imageUrl(item.images?.[0]);
  const ad = Math.abs(offset);
  const scale = Math.max(0.6, 1 - ad * 0.16);
  const rot = Math.max(-55, Math.min(55, -offset * 24));
  const style = {
    transform: `translateX(${offset * spacing}px) perspective(900px) rotateY(${rot}deg) scale(${scale})`,
    opacity: Math.max(0.35, 1 - ad * 0.2),
    zIndex: 50 - Math.round(ad * 4),
    visibility: ad > 4.5 ? "hidden" : "visible",
  };
  const open = () => (ad < 0.5 ? navigate(`/product/${item.id}`) : onSelect());

  return (
    <article data-testid={`thrift-item-${item.id}`} className="absolute top-0 left-1/2 -ml-[85px] w-[170px] origin-top will-change-transform" style={style}>
      <div className="rail-sway origin-top" style={{ animationDelay: `${-index * 0.65}s` }}>
        {cutout ? (
          <div className="relative">
            <div className="absolute inset-x-0 top-[50px] h-[300px] sm:h-[350px] overflow-visible"><Ghosts img={img} vel={vel} cut /></div>
            <HangingGarment cutout={cutout} item={item} onOpen={open} />
            {isNew && <span data-testid={`thrift-new-badge-${item.id}`} className="absolute top-[70px] left-0 z-20 rounded-full bg-[#C5A059] px-2 py-[3px] font-mono text-[8.5px] font-semibold uppercase tracking-[0.14em] text-[#0F0F0F] shadow-[0_2px_8px_rgba(0,0,0,0.3)]">{badge}</span>}
          </div>
        ) : (
        <>
        <RailHook price={item.price} />
        <div className="relative -mt-[2px]">
          <Ghosts img={img} vel={vel} cut={!!cutout} />
          <button type="button" onClick={open} data-testid={`thrift-item-select-${item.id}`} aria-label={item.title} className={`relative block ${CARD} ${cutout ? "bg-transparent" : "overflow-hidden bg-[#1C1C1C] shadow-[0_24px_40px_-18px_rgba(60,40,20,0.55)] ring-1 ring-[#0F0F0F]/10"}`}>
            {cutout ? <img src={cutout} alt={item.title} draggable={false} loading="lazy" data-testid="thrift-item-cutout" className="h-full w-full object-contain object-top select-none drop-shadow-[0_22px_18px_rgba(60,40,20,0.35)]" /> : img ? <img src={img} alt={item.title} draggable={false} loading="lazy" data-testid="thrift-item-photo" className="h-full w-full object-cover select-none" /> : <span className="h-full w-full flex items-center justify-center text-[#C5A059]"><ImageOff className="h-8 w-8" strokeWidth={1} /></span>}
            {isNew && <span data-testid={`thrift-new-badge-${item.id}`} className="absolute top-2.5 left-2.5 rounded-full bg-[#C5A059] px-2 py-[3px] font-mono text-[8.5px] font-semibold uppercase tracking-[0.14em] text-[#0F0F0F] shadow-[0_2px_8px_rgba(0,0,0,0.45)]">{badge}</span>}
            {!cutout && <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pt-10 pb-3 text-left">
              <span className="block text-[12px] leading-tight text-white truncate">{item.title}</span>
              <span className="block font-mono text-[11px] text-[#E3C78A] mt-0.5">{formatINR(item.price)}</span>
            </span>}
          </button>
        </div>
        </>
        )}
      </div>
      <div style={{ opacity: Math.max(0, Math.min(1, 1 - (ad - 1) * 0.9)), pointerEvents: ad > 1.6 ? "none" : "auto" }}>
        <ThriftInfo item={item} onToggleLike={onToggleLike} />
      </div>
    </article>
  );
};
